import type { ChatMessage, HealthMemoryContextSnippet, HealthMemoryItem, HealthMemoryStatus } from 'types';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { persistence } from 'services/persistence';

const STORAGE_KEY = STORAGE_KEYS.HEALTH_MEMORY_ITEMS;
const ENCRYPTION_KEY_STORAGE_KEY = STORAGE_KEYS.HEALTH_MEMORY_ENCRYPTION_KEY;
const ENCRYPTED_PREFIX = 'enc:v1:';

function nowIso(): string {
  return new Date().toISOString();
}

function clamp(value: number, min = 0, max = 1): number {
  return Math.max(min, Math.min(max, value));
}

function toCertainty(confidence: number): 'low' | 'medium' | 'high' {
  if (confidence >= 0.85) return 'high';
  if (confidence >= 0.6) return 'medium';
  return 'low';
}

function safeParse(value: string | null): HealthMemoryItem[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as HealthMemoryItem[]) : [];
  } catch {
    return [];
  }
}

function encodeBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function getSubtleCrypto(): SubtleCrypto | null {
  if (typeof window === 'undefined') return null;
  return window.crypto?.subtle || null;
}

async function getOrCreateEncryptionKey(): Promise<CryptoKey | null> {
  const subtle = getSubtleCrypto();
  if (!subtle || typeof window === 'undefined') {
    return null;
  }

  try {
    const existing = persistence.getItem(ENCRYPTION_KEY_STORAGE_KEY);
    if (existing) {
      const jwk = JSON.parse(existing);
      return await subtle.importKey(
        'jwk',
        jwk,
        { name: 'AES-GCM' },
        false,
        ['encrypt', 'decrypt']
      );
    }
  } catch {
    // Fall through and create a fresh key.
  }

  const key = await subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  const jwk = await subtle.exportKey('jwk', key);
  persistence.setJSON(ENCRYPTION_KEY_STORAGE_KEY, jwk);
  return key;
}

async function encryptString(plaintext: string): Promise<string> {
  const subtle = getSubtleCrypto();
  if (!subtle || typeof window === 'undefined') {
    return plaintext;
  }
  const key = await getOrCreateEncryptionKey();
  if (!key) {
    return plaintext;
  }

  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const encrypted = await subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  const payload = JSON.stringify({
    iv: encodeBase64(iv),
    data: encodeBase64(new Uint8Array(encrypted)),
  });
  return `${ENCRYPTED_PREFIX}${payload}`;
}

async function decryptString(value: string): Promise<string> {
  if (!value.startsWith(ENCRYPTED_PREFIX)) {
    return value;
  }
  const subtle = getSubtleCrypto();
  if (!subtle) {
    return '';
  }
  const key = await getOrCreateEncryptionKey();
  if (!key) {
    return '';
  }

  const payloadRaw = value.slice(ENCRYPTED_PREFIX.length);
  const payload = JSON.parse(payloadRaw) as { iv: string; data: string };
  const iv = decodeBase64(payload.iv);
  const encrypted = decodeBase64(payload.data);
  const decrypted = await subtle.decrypt({ name: 'AES-GCM', iv }, key, encrypted);
  return new TextDecoder().decode(decrypted);
}

export const healthMemoryService = {
  isEnabled(): boolean {
    const profile = persistence.getJSON<Record<string, unknown>>('eir-user-profile', {});
    return Boolean(profile?.memoryEnabled);
  },

  async loadItems(): Promise<HealthMemoryItem[]> {
    const stored = persistence.getItem(STORAGE_KEY);
    if (!stored) return [];

    let parsedItems = safeParse(stored);
    if (stored.startsWith(ENCRYPTED_PREFIX)) {
      try {
        const decrypted = await decryptString(stored);
        parsedItems = safeParse(decrypted);
      } catch {
        return [];
      }
    } else if (parsedItems.length > 0) {
      // Transparent one-time migration from plaintext local storage.
      await this.saveItems(parsedItems);
    }

    return parsedItems.filter((item) => item && item.id && item.label && item.category && item.status !== 'dismissed');
  },

  async saveItems(items: HealthMemoryItem[]): Promise<void> {
    const raw = JSON.stringify(items);
    const encrypted = await encryptString(raw);
    persistence.setItem(STORAGE_KEY, encrypted);
  },

  async clearAll(): Promise<void> {
    persistence.removeItem(STORAGE_KEY);
  },

  async updateStatus(itemId: string, status: HealthMemoryStatus): Promise<void> {
    const updated = (await this.loadItems()).map((item) => (
      item.id === itemId
        ? {
            ...item,
            status,
            certaintyLevel: status === 'dismissed' ? item.certaintyLevel : 'high',
            confidence: status === 'dismissed' ? item.confidence : clamp(Math.max(item.confidence, 0.92)),
            updatedAt: nowIso(),
          }
        : item
    ));
    await this.saveItems(updated);
  },

  /**
   * @deprecated Pattern-based extraction removed. Use LLM-based extractConditions API instead.
   * Kept as no-op for backward compatibility.
   */
  extractCandidates(_message: ChatMessage): HealthMemoryItem[] {
    return [];
  },

  async mergeCandidates(candidates: HealthMemoryItem[]): Promise<HealthMemoryItem[]> {
    if (!candidates.length) return this.loadItems();
    const existing = await this.loadItems();
    const byKey = new Map<string, HealthMemoryItem>();
    for (const item of existing) {
      const key = `${item.category}:${item.label.toLowerCase()}`;
      byKey.set(key, item);
    }

    for (const candidate of candidates) {
      const key = `${candidate.category}:${candidate.label.toLowerCase()}`;
      const found = byKey.get(key);
      if (!found) {
        byKey.set(key, candidate);
        continue;
      }

      const merged: HealthMemoryItem = {
        ...found,
        detail: found.detail || candidate.detail,
        confidence: clamp(Math.max(found.confidence, candidate.confidence)),
        certaintyLevel: toCertainty(Math.max(found.confidence, candidate.confidence)),
        evidenceRefs: [...found.evidenceRefs, ...candidate.evidenceRefs].slice(-12),
        updatedAt: nowIso(),
      };
      byKey.set(key, merged);
    }

    const mergedValues = Array.from(byKey.values())
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 250);
    await this.saveItems(mergedValues);
    return mergedValues;
  },

  async buildRetrievalSnippets(userMessage: string, limit = 8): Promise<HealthMemoryContextSnippet[]> {
    const items = (await this.loadItems()).filter((item) => item.status !== 'dismissed');
    if (!items.length) return [];

    const recencyFloor = Math.min(3, Math.floor(limit / 2));
    const byUpdated = [...items].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
    const recentIds = new Set(byUpdated.slice(0, recencyFloor).map((i) => i.id));

    const query = userMessage.toLowerCase();
    const queryRanked = items
      .filter((item) => !recentIds.has(item.id))
      .map((item) => {
        const labelHit = query.includes(item.label.toLowerCase()) ? 0.8 : 0;
        const categoryHit = query.includes(item.category) ? 0.4 : 0;
        const recency = Math.max(0, 1 - (Date.now() - new Date(item.updatedAt).getTime()) / (1000 * 60 * 60 * 24 * 60));
        const score = item.confidence + labelHit + categoryHit + recency;
        return { item, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, Math.max(0, limit - recencyFloor));

    const recentSnippets = byUpdated.slice(0, recencyFloor).map((item) => ({
      id: item.id,
      category: item.category,
      label: item.label,
      detail: item.detail,
      confidence: item.confidence,
      certaintyLevel: item.certaintyLevel,
      status: item.status,
    }));
    const relevantSnippets = queryRanked.map(({ item }) => ({
      id: item.id,
      category: item.category,
      label: item.label,
      detail: item.detail,
      confidence: item.confidence,
      certaintyLevel: item.certaintyLevel,
      status: item.status,
    }));
    const ranked = [...recentSnippets, ...relevantSnippets].slice(0, Math.max(1, limit));

    const lastUsedAt = nowIso();
    const updates = items.map((item) => (
      ranked.some((snippet) => snippet.id === item.id)
        ? { ...item, lastUsedAt, updatedAt: item.updatedAt }
        : item
    ));
    await this.saveItems(updates);
    return ranked;
  },
};

export const HEALTH_MEMORY_STORAGE_KEY = STORAGE_KEY;
export const HEALTH_MEMORY_ENCRYPTION_KEY_STORAGE_KEY = ENCRYPTION_KEY_STORAGE_KEY;

/**
 * Restore health memory items from an imported .eir file.
 * Only merges when memory is enabled and items are provided.
 */
export async function restoreHealthMemoryFromImport(
  healthMemoryItems: HealthMemoryItem[] | undefined
): Promise<void> {
  if (!healthMemoryService.isEnabled() || !healthMemoryItems || healthMemoryItems.length === 0) {
    return;
  }
  await healthMemoryService.mergeCandidates(healthMemoryItems);
}
